// features/settings/pages/TerminalsPage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { SettingsModal } from '../components/SettingsModal';
import * as api from '../services/settingsApi';
import {
    Monitor, Plus, Save, ArrowLeft, Clock, MonitorSmartphone,
    Tablet, Smartphone, Wifi, WifiOff, Pencil, Trash2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface PosTerminal {
    id: string;
    name: string;
    branchId: string | null;
    terminalType: string;
    isActive: boolean;
    lastSeenAt: string | null;
}

const TYPE_META: Record<string, { icon: React.ElementType; label: string; gradient: string }> = {
    desktop: { icon: Monitor, label: 'Desktop', gradient: 'from-blue-500 to-indigo-600' },
    tablet: { icon: Tablet, label: 'Tablet', gradient: 'from-purple-500 to-violet-600' },
    mobile: { icon: Smartphone, label: 'Mobile', gradient: 'from-amber-500 to-orange-600' },
};

export function TerminalsPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const [showForm, setShowForm] = useState(false);
    const [editingTerminal, setEditingTerminal] = useState<PosTerminal | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<PosTerminal | null>(null);
    const [form, setForm] = useState({ name: '', terminalType: 'desktop' });

    const { data: terminals, isLoading } = useQuery<PosTerminal[]>({
        queryKey: ['pos-terminals', user?.tenantId],
        queryFn: () => api.getPosTerminals(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const saveMutation = useMutation({
        mutationFn: (payload: any) => editingTerminal
            ? api.updatePosTerminal(user!.tenantId, editingTerminal.id, payload)
            : api.createPosTerminal(user!.tenantId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pos-terminals'] });
            addToast({ type: 'success', title: editingTerminal ? 'Updated' : 'Registered' });
            closeForm();
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.message }),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.deletePosTerminal(user!.tenantId, id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pos-terminals'] });
            setDeleteConfirm(null);
            addToast({ type: 'success', title: 'Deleted' });
        },
    });

    const handleEdit = (t: PosTerminal) => {
        setEditingTerminal(t);
        setForm({ name: t.name, terminalType: t.terminalType });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingTerminal(null);
        setForm({ name: '', terminalType: 'desktop' });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-14 h-14 rounded-2xl border-4 border-cyan-200 border-t-cyan-600" />
            </div>
        );
    }

    const activeTerminals = (terminals || []).filter(t => t.isActive).length;
    const onlineNow = (terminals || []).filter(t => t.lastSeenAt && new Date(t.lastSeenAt).getTime() > Date.now() - 10 * 60 * 1000).length;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50/30">
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/settings')} className="p-2 rounded-xl hover:bg-slate-100" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-cyan-500 to-sky-600 rounded-2xl shadow-lg shadow-cyan-500/25">
                                <Monitor className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">POS Terminals</h1>
                                <p className="text-sm text-slate-500">{terminals?.length || 0} device{(terminals?.length || 0) !== 1 ? 's' : ''} · {activeTerminals} active · {onlineNow} online</p>
                            </div>
                        </div>
                        <button onClick={() => { closeForm(); setShowForm(true); }}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-sky-600 text-white rounded-xl hover:from-cyan-600 transition-all font-semibold shadow-lg text-sm">
                            <Plus className="w-4 h-4" /> Register Terminal
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { label: 'Total Devices', value: terminals?.length || 0, icon: Monitor, gradient: 'from-cyan-500 to-sky-600' },
                        { label: 'Active', value: activeTerminals, icon: Wifi, gradient: 'from-emerald-500 to-teal-600' },
                        { label: 'Online Now', value: onlineNow, icon: Wifi, gradient: 'from-green-500 to-emerald-600' },
                        { label: 'Offline', value: activeTerminals - onlineNow, icon: WifiOff, gradient: 'from-rose-500 to-pink-600' },
                    ].map((stat, i) => (
                        <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all duration-300">
                            <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{stat.label}</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value.toLocaleString()}</p>
                                </div>
                                <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-lg`}>
                                    <stat.icon className="w-5 h-5 text-white" />
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Empty State */}
                {(!terminals || terminals.length === 0) ? (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="text-center py-20 bg-white rounded-3xl border border-slate-200">
                        <div className="w-20 h-20 bg-cyan-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <Monitor className="w-10 h-10 text-cyan-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-700">No terminals registered</h3>
                        <p className="text-slate-400 mt-2">Register POS devices for your branches to start processing sales.</p>
                        <button onClick={() => { closeForm(); setShowForm(true); }}
                            className="mt-6 px-6 py-3 bg-gradient-to-r from-cyan-500 to-sky-600 text-white rounded-xl font-semibold shadow-lg inline-flex items-center gap-2">
                            <Plus className="w-5 h-5" /> Register First Terminal
                        </button>
                    </motion.div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(terminals || []).map((terminal, i) => {
                            const meta = TYPE_META[terminal.terminalType] || TYPE_META.desktop;
                            const Icon = meta.icon;
                            const isOnline = terminal.lastSeenAt && new Date(terminal.lastSeenAt).getTime() > Date.now() - 10 * 60 * 1000;
                            return (
                                <motion.div key={terminal.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                    whileHover={{ y: -4 }}
                                    className="group bg-white rounded-2xl border-2 border-slate-200 shadow-sm hover:shadow-lg hover:border-cyan-300 transition-all duration-300 overflow-hidden">
                                    <div className="p-5">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center shadow-md`}>
                                                    <Icon size={22} className="text-white" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-slate-900">{terminal.name}</h3>
                                                    <p className="text-xs text-slate-500">{meta.label}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                {isOnline && <Badge variant="success" size="sm">Online</Badge>}
                                                <Badge variant={terminal.isActive ? 'success' : 'danger'} size="sm">
                                                    {terminal.isActive ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </div>
                                        </div>
                                        {terminal.lastSeenAt && (
                                            <div className="flex items-center gap-1.5 text-xs text-slate-400 pt-4 border-t border-slate-100">
                                                <Clock size={12} /> Last seen: {new Date(terminal.lastSeenAt).toLocaleString()}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 pt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => handleEdit(terminal)}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg">
                                                <Pencil size={12} /> Edit
                                            </button>
                                            <button onClick={() => setDeleteConfirm(terminal)}
                                                className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-rose-500 hover:bg-rose-50 rounded-lg">
                                                <Trash2 size={12} /> Delete
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* Modal */}
                <SettingsModal
                    open={showForm}
                    onClose={closeForm}
                    title={editingTerminal ? 'Edit Terminal' : 'Register Terminal'}
                    subtitle="Add a new POS device"
                    icon={Monitor}
                    footer={
                        <>
                            <Button variant="secondary" onClick={closeForm}>Cancel</Button>
                            <Button leftIcon={<Save size={16} />} onClick={() => saveMutation.mutate(form)}
                                isLoading={saveMutation.isPending} disabled={!form.name}>
                                {editingTerminal ? 'Update' : 'Register'}
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <Input label="Terminal Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Front Counter 1" required />
                        <div>
                            <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Device Type</label>
                            <select value={form.terminalType} onChange={(e) => setForm({ ...form, terminalType: e.target.value })}
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20">
                                <option value="desktop">Desktop Computer</option>
                                <option value="tablet">Tablet</option>
                                <option value="mobile">Mobile Device</option>
                            </select>
                        </div>
                    </div>
                </SettingsModal>

                {/* Delete Confirmation */}
                <AnimatePresence>
                    {deleteConfirm && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
                                <div className="text-center">
                                    <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4"><Trash2 className="w-8 h-8 text-rose-500" /></div>
                                    <h2 className="text-xl font-bold text-slate-900">Delete "{deleteConfirm.name}"?</h2>
                                    <p className="text-sm text-slate-500 mt-2">This terminal will be permanently removed.</p>
                                </div>
                                <div className="flex gap-3 mt-8">
                                    <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl font-medium text-sm">Cancel</button>
                                    <button onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                                        className="flex-1 px-5 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl hover:from-rose-600 transition-all font-semibold text-sm shadow-lg shadow-rose-500/25">
                                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}